// src/Pages/DoctorDashboard/PatientDetails.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Avatar,
  Tabs,
  Tab,
  Button,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Phone,
  Email,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPatientDetails } from '../../features/patients/patientSlice';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

import PatientAppointments from '../../components/PatientAppointments';
import PatientPrescriptions from '../../components/PatientPrescriptions';
import PatientMedications from '../../components/PatientMedications';
import PatientNotes from '../../components/PatientNotes';
import AppointmentsList from '../../components/DoctorDashboard/AppointmentsList';
import PatientOverview from '../../components/PatientOverview';
import EnhancedPatientOverview from '../../components/PatientOverview/EnhancedPatientOverview';
import ClinicalNotes from '../../components/ClinicalNotes';
import EmergencyContacts from '../../components/EmergencyContacts';
import MentalHealthAssessment from '../../components/MentalHealthAssessment';
import EnhancedMentalHealthAssessment from '../../components/MentalHealthAssessment/EnhancedMentalHealthAssessment';
import PatientDocuments from '../../components/PatientDocuments';
import EnhancedPatientDocuments from '../../components/PatientDocuments/EnhancedPatientDocuments';
import PatientIntakeForm from '../../components/PatientIntakeForm';
import EnhancedPatientIntakeForm from '../../components/PatientOnboarding/PatientIntakeForm';
import TreatmentHistory from '../../components/TreatmentHistory';
import EnhancedPatientHistory from './PatientHistory/EnhancedPatientHistory';
import TreatmentPlan from '../../components/TreatmentPlan';
import EnhancedTreatmentPlan from '../../components/TreatmentPlan/EnhancedTreatmentPlan';
import PatientDiagnosis from '../../components/PatientDiagnosis';

const PatientDetails = ({ patientId }) => {
  const theme = useTheme();
  // Use `patientId` from props if provided, otherwise get it from URL params.
  const { id } = useParams();
  const finalPatientId = patientId || id;

  const dispatch = useDispatch();
  const patientsState = useSelector((state) => state.patients);
  const selectedPatient = patientsState?.selectedPatient || null;
  const patientAppointments = patientsState?.patientAppointments || [];
  const loading = patientsState?.selectedLoading || false;
  const error = patientsState?.selectedError || null;

  const [tabValue, setTabValue] = useState(0);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (finalPatientId) {
      console.log('📋 Loading patient details for ID:', finalPatientId);
      dispatch(getPatientDetails(finalPatientId))
        .unwrap()
        .then((data) => {
          console.log('✅ Patient details loaded:', data);
          setRetryCount(0);
        })
        .catch((err) => {
          console.error('❌ Error loading patient details:', err);
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
            }, 1000 * (retryCount + 1));
          }
        });
    }
  }, [dispatch, finalPatientId, retryCount]);

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDownloadPDF = () => {
    const input = document.getElementById('patient-details');
    html2canvas(input).then((canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF();
      pdf.addImage(imgData, 'PNG', 0, 0);
      pdf.save(`${selectedPatient?.patient?.name || 'Patient'}_details.pdf`);
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          {typeof error === 'string' ? error : error?.detail || 'Failed to load patient details'}
        </Typography>
      </Box>
    );
  }

  if (!selectedPatient) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Patient not found. Please select a patient from the patient list.</Typography>
      </Box>
    );
  }

  const patient = selectedPatient;
  const appointments = patientAppointments || [];
  const patientStats = patient?.stats || {}; // From backend response

  const tabs = [
    { label: 'Overview', value: 'overview', icon: 'Person' },
    { label: 'Appointments', value: 'appointments', icon: 'CalendarToday', badge: appointments.length },
    { label: 'Clinical Notes', value: 'clinical-notes', icon: 'Assignment' },
    { label: 'Prescriptions', value: 'prescriptions', icon: 'LocalPharmacy' },
    { label: 'Documents', value: 'documents', icon: 'Description', badge: patientStats.documents_count || 0 },
    { label: 'Treatment Plan', value: 'treatment-plan', icon: 'MedicalServices' },
    { label: 'Treatment History', value: 'treatment-history', icon: 'History' },
    { label: 'Emergency Contacts', value: 'emergency-contacts', icon: 'LocalHospital' },
    { label: 'Mental Health', value: 'mental-health-assessment', icon: 'Psychology' },
    { label: 'Diagnosis', value: 'diagnosis', icon: 'LocalHospital' },
  ];

  const renderTabContent = () => {
    switch (tabValue) {
      case 'overview':
        return <EnhancedPatientOverview patient={patient} role="doctor" />;
      case 'clinical-notes':
        return <ClinicalNotes patientId={finalPatientId} />;
      case 'emergency-contacts':
        return <EmergencyContacts patientId={finalPatientId} />;
      case 'mental-health-assessment':
        return <EnhancedMentalHealthAssessment patientId={finalPatientId} role="doctor" />;
      case 'documents':
        return <EnhancedPatientDocuments patientId={finalPatientId} role="doctor" />;
      case 'intake-form':
        return <EnhancedPatientIntakeForm 
          patientId={finalPatientId} 
          onSaved={() => {
            // Refresh patient data after intake form is saved
            if (finalPatientId) {
              dispatch(getPatientDetails(finalPatientId));
            }
          }}
        />;
      case 'treatment-history':
        return <EnhancedPatientHistory patientId={finalPatientId} role="doctor" />;
      case 'treatment-plan':
        return <EnhancedTreatmentPlan patientId={finalPatientId} role="doctor" />;
      case 'appointments':
        return <PatientAppointments patientId={finalPatientId} />;
      case 'prescriptions':
        return <PatientPrescriptions patientId={finalPatientId} />;
      case 'medications':
        return <PatientMedications patientId={finalPatientId} />;
      case 'notes':
        return <PatientNotes patientId={finalPatientId} />;
      case 'diagnosis':
        return <PatientDiagnosis patientId={finalPatientId} />;
      default:
        return null;
    }
  };

  // Extract patient data safely
  const patientName = patient?.name || patient?.patient?.name || 'Unknown Patient';
  const patientAge = patient?.age || patient?.date_of_birth 
    ? (new Date().getFullYear() - new Date(patient.date_of_birth || patient?.patient?.date_of_birth).getFullYear())
    : null;
  const patientGender = patient?.gender || patient?.patient?.gender || 'N/A';
  const patientPhone = patient?.phone_number || patient?.patient?.phone_number || 'N/A';
  const patientEmail = patient?.email || patient?.patient?.email || 'N/A';
  const patientAvatar = patient?.avatar || patient?.profile_image || null;

  return (
    <Box sx={{ padding: { xs: 2, sm: 3 } }}>
      {/* Patient Header Card */}
      <Paper 
        sx={{ 
          padding: 3, 
          marginBottom: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
          borderRadius: 2,
          boxShadow: 2
        }} 
        id="patient-details"
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item>
            <Avatar 
              src={patientAvatar}
              sx={{ width: 100, height: 100, fontSize: '2.5rem', border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
            >
              {patientName.charAt(0).toUpperCase()}
            </Avatar>
          </Grid>
          <Grid item xs>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              {patientName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
              {patientAge && (
                <Typography variant="body1" color="text.secondary">
                  Age: {patientAge}
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary">
                Gender: {patientGender}
              </Typography>
              <Chip 
                icon={patient?.profile_completed || patient?.patient_profile_completed ? <CheckCircleIcon /> : <PendingIcon />}
                label={patient?.profile_completed || patient?.patient_profile_completed ? 'Profile Complete' : 'Profile Incomplete'}
                size="small"
                color={patient?.profile_completed || patient?.patient_profile_completed ? 'success' : 'warning'}
              />
            </Box>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Phone fontSize="small" color="action" />
                  <Typography variant="body2">{patientPhone}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email fontSize="small" color="action" />
                  <Typography variant="body2">{patientEmail}</Typography>
                </Box>
              </Grid>
            </Grid>
            
            {/* Patient Stats Summary */}
            {patientStats && Object.keys(patientStats).length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                <Chip 
                  label={`${patientStats.total_appointments || 0} Total Appointments`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Chip 
                  label={`${patientStats.completed_appointments || 0} Completed`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
                <Chip 
                  label={`${patientStats.upcoming_appointments || 0} Upcoming`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
                {patientStats.documents_count > 0 && (
                  <Chip 
                    label={`${patientStats.documents_count} Documents`}
                    size="small"
                    color="default"
                    variant="outlined"
                  />
                )}
              </Box>
            )}
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleDownloadPDF}
                startIcon={<DescriptionIcon />}
              >
                Download PDF
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ marginBottom: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleChange} 
          aria-label="patient details tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 72,
            }
          }}
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={tab.value} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{tab.label}</span>
                  {tab.badge !== undefined && tab.badge > 0 && (
                    <Chip 
                      label={tab.badge} 
                      size="small" 
                      sx={{ 
                        height: 20, 
                        minWidth: 20,
                        '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' }
                      }} 
                    />
                  )}
                </Box>
              }
              id={`patient-tab-${index}`}
              aria-controls={`patient-tabpanel-${index}`}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ marginTop: 2 }}>
        {renderTabContent()}
      </Box>
    </Box>
  );
};

export default PatientDetails;
